import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FileDown, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { requestBrochure } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';

export const BrochureDownload = ({ property }) => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', email: user?.email || '' });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2 || form.phone.replace(/\D/g, '').length < 10) {
      toast.error('Please enter your name and a valid 10-digit mobile number');
      return;
    }
    setBusy(true);
    try {
      const res = await requestBrochure({ property_id: property.property_id, ...form });
      setResult(res);
      if (res.brochure_url) window.open(res.brochure_url, '_blank', 'noopener');
      toast.success('Thanks! Your brochure request is registered.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="w-full h-12 border-[#D4AF37] text-[#0F172A] hover:bg-[#D4AF37]/10" data-testid="download-brochure-btn">
        <FileDown className="h-4 w-4 mr-2" /> Download Brochure
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="brochure-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">Get the {property.project_name || 'project'} brochure</DialogTitle>
            <DialogDescription>Share your details and we'll unlock the brochure instantly. Our advisor will also WhatsApp you the latest price list.</DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="py-4 text-center space-y-3" data-testid="brochure-success">
              <CheckCircle2 className="h-10 w-10 text-green-600 mx-auto" />
              <p className="font-medium text-[#0F172A]">Request received · Ref {result.lead_id}</p>
              {result.brochure_url ? (
                <a href={result.brochure_url} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-[#0F172A] underline" data-testid="brochure-open-link">Open brochure (PDF)</a>
              ) : (
                <p className="text-sm text-muted-foreground">Our team will WhatsApp you the brochure within 24 hours.</p>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <Input placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="brochure-name" />
              <Input placeholder="Mobile number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="brochure-phone" />
              <Input type="email" placeholder="Email (optional)" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="brochure-email" />
              <Button type="submit" className="w-full btn-gold" disabled={busy} data-testid="brochure-submit">
                {busy ? 'Please wait…' : 'Get Brochure'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
